import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent, ReactNode, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { HeadingProps, LiProps } from 'react-markdown/lib/ast-to-react';
import { cn } from 'utils/cn';
import ModalWithActivator from './ModalWithActivator';

interface ExtraDescriptionModalProps {
  activator?: ReactNode;
  description: string;
  extraName: string;
  modalClass?: string;
  activatorClass?: string;
  withMargin?: boolean;
  withPreline?: boolean;
}

const ExtraDescriptionModal: FunctionComponent<ExtraDescriptionModalProps> = ({
  activator,
  description,
  extraName,
  modalClass,
  activatorClass,
  withMargin = true,
  withPreline = false,
}) => {
  const { t } = useTranslation('common');
  const multilineDescription = useMemo(
    () => description.replace(/(\\n)/g, '\n\n'),
    [description]
  );

  const renderedHeader = ({ node, level, ...props }: HeadingProps) => (
    <span className="mb-2 block text-base font-medium" {...props} />
  );

  const renderedListItem = ({
    node,
    index,
    ordered,
    checked,
    className,
    ...props
  }: LiProps) => <li className="my-2 block text-sm last:mb-5" {...props} />;

  return (
    <ModalWithActivator
      activatorClass={cn(
        !activator
          ? `${
              withMargin ? 'ml-7' : ''
            } text-sm text-slate-500 underline hover:no-underline`
          : '',
        activatorClass
      )}
      activator={activator ? activator : <span>{t('common:showMore')}</span>}
      modalTitle={extraName}
      modalClass={classNames(modalClass)}
    >
      <ReactMarkdown
        className={withPreline ? 'whitespace-pre-line' : ''}
        components={{
          h1: renderedHeader,
          h2: renderedHeader,
          h3: renderedHeader,
          h4: renderedHeader,
          h5: renderedHeader,
          h6: renderedHeader,
          li: renderedListItem,
        }}
      >
        {multilineDescription}
      </ReactMarkdown>
    </ModalWithActivator>
  );
};

export default ExtraDescriptionModal;
